import { HStack, Spacer, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { LuPencil, LuPlus, LuWrench } from "react-icons/lu";
import { useParams } from "react-router-dom";

import {
  deleteBusinessFeature,
  getBusinessFeatures,
  getCustomerProfileSubsets,
} from "../../../api/company-knowledge";
import { BusinessFeatureDialog } from "../../../components/business-feature-dialog";
import { Button } from "../../../components/button";
import DataTable, { DataTableColumn } from "../../../components/data-table";
import { DeleteButton } from "../../../components/delete-button";
import { EmptyState } from "../../../components/empty-state";
import IconCircle from "../../../components/icon-circle";
import PageBody from "../../../components/page-body";
import { useLoading, useToaster } from "../../../hooks";
import {
  BusinessFeature,
  CustomerProfileSubset,
} from "../../../types/company-knowledge";

export default function BusinessFeaturesPage() {
  const { workspace: workspaceSlug } = useParams();
  const [features, setFeatures] = React.useState<BusinessFeature[]>([]);
  const [subsets, setSubsets] = React.useState<CustomerProfileSubset[]>([]);
  const [isLoading, withLoading] = useLoading();
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editingFeature, setEditingFeature] =
    React.useState<BusinessFeature | null>(null);
  const withToaster = useToaster();

  const loadFeatures = React.useCallback(async () => {
    if (!workspaceSlug) return;
    await withLoading(async () => {
      await withToaster(async () => {
        // The subsets ride along on every load so the "Applicable
        // ICP Subsets" column and the dialog's checkboxes stay in
        // sync.
        const [features, subsets] = await Promise.all([
          getBusinessFeatures(workspaceSlug),
          getCustomerProfileSubsets(workspaceSlug),
        ]);
        setFeatures(features);
        setSubsets(subsets);
        setHasLoadedOnce(true);
      });
    });
  }, [withLoading, withToaster, workspaceSlug]);

  React.useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  const handleDelete = React.useCallback(
    async (id: number) => {
      if (!workspaceSlug) return;
      await withLoading(async () => {
        await withToaster(async () => {
          await deleteBusinessFeature(workspaceSlug, id);
          await loadFeatures();
        });
      });
    },
    [loadFeatures, withLoading, withToaster, workspaceSlug],
  );

  const columns: DataTableColumn<BusinessFeature>[] = [
    {
      id: "name",
      name: "Name",
      icon: <LuWrench />,
      width: 240,
      cell: ({ value }) => <Text fontWeight="medium">{value.name}</Text>,
    },
    {
      id: "problem-domain",
      name: "Problem it addresses",
      width: 320,
      cell: ({ value }) => (
        <Text color="fg.muted" truncate>
          {value["problem-domain"] || "—"}
        </Text>
      ),
    },
    {
      name: "Applicable ICP subsets",
      width: 200,
      cell: ({ value }) => {
        const names = subsets
          .filter((s) => value["subset-ids"].includes(s.id))
          .map((s) => s.name);
        return (
          <Text color="fg.muted" truncate>
            {names.length > 0 ? names.join(", ") : "All subsets"}
          </Text>
        );
      },
    },
    {
      name: "",
      width: 100,
      cell: ({ value }) => (
        <HStack justify="flex-end">
          <Button
            size="sm"
            title="Edit feature"
            variant="ghost"
            onClick={() => {
              setEditingFeature(value);
              setIsDialogOpen(true);
            }}
          >
            <LuPencil />
          </Button>
          <DeleteButton
            label="Delete"
            message={`Delete "${value.name}"?`}
            onDelete={() => handleDelete(value.id)}
          />
        </HStack>
      ),
    },
  ];

  const isEmpty = features.length === 0 && hasLoadedOnce;

  return (
    <PageBody>
      <Stack flex="1" gap="5">
        <HStack>
          <Stack gap="0.5">
            <Text color="fg.ink" fontSize="md" fontWeight="medium">
              Features
            </Text>
            <Text color="fg.muted" fontSize="sm">
              What your product does, the problems it solves, and what it
              replaces.
            </Text>
          </Stack>
          {!isEmpty && (
            <>
              <Spacer />
              <Button
                onClick={() => {
                  setEditingFeature(null);
                  setIsDialogOpen(true);
                }}
              >
                <LuPlus /> Create Feature
              </Button>
            </>
          )}
        </HStack>
        {isEmpty ? (
          <HStack flex="1" alignItems="center">
            <EmptyState
              icon={<IconCircle Icon={LuWrench} size="md" variant="flat" />}
              title="No Business Features"
              description="Add the features your messaging should know about."
            >
              <Button
                onClick={() => {
                  setEditingFeature(null);
                  setIsDialogOpen(true);
                }}
              >
                <LuPlus /> Create Feature
              </Button>
            </EmptyState>
          </HStack>
        ) : (
          <DataTable
            data={features}
            columns={columns}
            skeleton={isLoading && hasLoadedOnce ? 3 : undefined}
          />
        )}
      </Stack>
      <BusinessFeatureDialog
        feature={editingFeature}
        isOpen={isDialogOpen}
        onOpenChange={({ open }) => {
          if (!open) setEditingFeature(null);
          setIsDialogOpen(open);
        }}
        onSuccess={loadFeatures}
        subsets={subsets}
        workspaceSlug={workspaceSlug ?? ""}
      />
    </PageBody>
  );
}
